import { Link, useTenantPathname } from "@/routing/accountRouting";
import { Stack, NavLink, ActionIcon, Tooltip } from "@mantine/core";
import {
  IconHome,
  IconMessage,
  IconAnalyze,
  IconLayoutDashboard,
  IconReport,
  IconTelescope,
  IconCode,
  IconDatabase,
  IconArrowsTransferDown,
  IconKey,
  IconLayersLinked,
  IconBook,
  IconTargetArrow,
  IconPlugConnected,
  IconRocket,
  IconBell,
  IconShieldSearch,
  IconFilter,
  IconRobot,
  IconReportMoney,
  IconPlug,
  IconMessageQuestion,
  IconShoppingBag,
  IconSparkles,
  IconUserCircle,
  IconRoute,
} from "@tabler/icons-react";
import { submenuFeatureFlag } from "@/constants/menuRegistry";
import { ANALYST_READER_ROLES } from "@/components/analyst/analystAccess";
import { useSectionAccess } from "@/hooks/useSectionAccess";
import { HiddenFromCustomerMark } from "@/components/auth/HiddenFromCustomer";
import { useAuth } from "@/context/AuthContext";
import type { AccountRole } from "@/types/auth";

type Section = "insights" | "activation" | "data-management" | "automations";

type NavItem = {
  key: string;
  label: string;
  to: string;
  icon: React.ReactNode;
  match: (pathname: string) => boolean;
  /**
   * Account feature flag this item additionally requires. The submenu gate
   * still applies on top — a flagged item is hidden when either says no.
   */
  featureFlag?: string;
  /**
   * Account roles that see this item, with the same semantics as
   * `ProtectedRoute accountRoles` (a superadmin always passes). The route
   * keeps its own `ProtectedRoute` gate: hiding the item is presentation,
   * the route check is what refuses the page.
   */
  accountRoles?: AccountRole[];
};

type Viewer = {
  /** `useSectionAccess().submenu(...).shown`: the customer view, or every
   *  flag-on section for a superadmin. */
  visible: boolean;
  flags: Record<string, boolean> | null | undefined;
  role: AccountRole | undefined;
  isSuperadmin: boolean;
};

/** The one rule SectionSubNav filters by; exported for its test. */
export function isNavItemShown(item: NavItem, viewer: Viewer): boolean {
  if (!viewer.visible) return false;
  if (item.featureFlag && viewer.flags?.[item.featureFlag] !== true) return false;
  if (item.accountRoles && !viewer.isSuperadmin) {
    return !!viewer.role && item.accountRoles.includes(viewer.role);
  }
  return true;
}

// Exported for the landing-path drift test (utils/landingPath.test.ts), which
// asserts SECTION_SUBMENU_LEAVES paths stay in sync with these nav routes.
export const SECTION_ITEMS: Record<Section, NavItem[]> = {
  insights: [
    {
      key: "home",
      label: "Home",
      to: "/insights",
      icon: <IconHome size="1rem" stroke={1.5} />,
      // Exact match, unlike the startsWith() siblings below: "/insights" is a
      // prefix of every other Insights route, so startsWith would light Home up
      // on the chat, deep-dive and dashboard pages too.
      match: (p) => p === "/insights",
      // Built, not released. With the flag off /insights redirects to
      // Dashboards, so showing this item would point at a redirect.
      featureFlag: submenuFeatureFlag("insights", "home"),
    },
    {
      key: "chat",
      label: "Chat",
      to: "/insights/chat",
      icon: <IconMessage size="1rem" stroke={1.5} />,
      match: (p) => p.startsWith("/insights/chat"),
    },
    {
      key: "deep-dives",
      label: "Deep Dives",
      to: "/insights/deep-dives",
      icon: <IconAnalyze size="1rem" stroke={1.5} />,
      match: (p) => p.startsWith("/insights/deep-dives"),
    },
    {
      key: "dashboards",
      label: "Dashboards",
      to: "/insights/dashboards",
      icon: <IconLayoutDashboard size="1rem" stroke={1.5} />,
      match: (p) => p.startsWith("/insights/dashboards"),
    },
    {
      key: "reports",
      label: "Reports",
      // Routes predate the menu entry and delivery e-mails may link to
      // /reports/:id — the URLs stay put, only the gate lives under Insights.
      to: "/reports",
      icon: <IconReport size="1rem" stroke={1.5} />,
      match: (p) => p.startsWith("/reports"),
    },
    {
      key: "analyst",
      label: "Analyst",
      to: "/analyst",
      icon: <IconTelescope size="1rem" stroke={1.5} />,
      // Every /analyst page, deliberately. Metrics and Open had their own nav
      // items for one day (plan 2026-09-19 § 6e) and are tabs on this page
      // now, so this item lights on all of them — including the metric
      // drill-down and a run document. A page whose tab is open while the
      // sidebar points nowhere is the failure this covers.
      match: (p) => p === "/analyst" || p.startsWith("/analyst/"),
      // Built, not released. With the flag off there are no schedules and
      // the feed is empty by construction, so showing the item would point
      // at a dead end. Same treatment as Insights Home above.
      featureFlag: submenuFeatureFlag("insights", "analyst"),
      // The analyst's readers, the same list the /analyst routes pin (plan
      // 2026-10-24-whatsapp-daily-summary § D8): MEMBERs read their own
      // workspaces' analyses; VIEWER and SUPPORT do not get the item.
      accountRoles: ANALYST_READER_ROLES,
    },
  ],
  "data-management": [
    {
      key: "sources",
      label: "Sources",
      to: "/data-management/sources",
      icon: <IconDatabase size="1rem" stroke={1.5} />,
      // /lineage is a TAB on the sources page (guarded by the sources submenu),
      // so it keeps this entry lit rather than leaving the nav with nothing active.
      match: (p) =>
        p === "/data-management" ||
        p.startsWith("/data-management/sources") ||
        p.startsWith("/data-management/lineage"),
    },
    {
      key: "flows",
      label: "Flows",
      to: "/data-management/flows",
      icon: <IconArrowsTransferDown size="1rem" stroke={1.5} />,
      match: (p) => p.startsWith("/data-management/flows"),
    },
    {
      key: "integrations",
      label: "Integrations",
      to: "/data-management/integrations",
      icon: <IconPlug size="1rem" stroke={1.5} />,
      match: (p) => p.startsWith("/data-management/integrations"),
    },
    {
      key: "authorizations",
      label: "Authorizations",
      to: "/data-management/authorizations",
      icon: <IconKey size="1rem" stroke={1.5} />,
      match: (p) => p.startsWith("/data-management/authorizations"),
    },
    {
      key: "destinations",
      label: "Destinations",
      to: "/data-management/destinations",
      icon: <IconPlugConnected size="1rem" stroke={1.5} />,
      match: (p) =>
        p.startsWith("/data-management/destinations") ||
        p.startsWith("/data-management/kv-destinations"),
    },
    {
      key: "semantic-layer",
      label: "Semantic Layer",
      to: "/data-management/semantic-layer",
      icon: <IconLayersLinked size="1rem" stroke={1.5} />,
      match: (p) => p.startsWith("/data-management/semantic-layer"),
    },
    {
      key: "glossary",
      label: "Business Context",
      to: "/data-management/glossary",
      icon: <IconBook size="1rem" stroke={1.5} />,
      match: (p) => p.startsWith("/data-management/glossary"),
    },
    {
      key: "targets",
      label: "Targets",
      to: "/data-management/targets",
      icon: <IconTargetArrow size="1rem" stroke={1.5} />,
      match: (p) => p.startsWith("/data-management/targets"),
      // Same audience as the route (Arjen 09-10, plan
      // 2026-10-09-targets-page-redesign-and-grid-entry OQ5).
      accountRoles: ["OWNER", "ADMIN"],
    },
    {
      key: "agents",
      label: "Agents",
      to: "/data-management/agents",
      icon: <IconRobot size="1rem" stroke={1.5} />,
      match: (p) => p.startsWith("/data-management/agents"),
    },
    {
      key: "financial-statements",
      label: "Financial Statements",
      to: "/data-management/financial-statements",
      icon: <IconReportMoney size="1rem" stroke={1.5} />,
      match: (p) => p.startsWith("/data-management/financial-statements"),
      // Ships dark (plan 2026-10-05-pnl-statement-mode): hidden until
      // `financial_statements_enabled` is set per account; the submenu gate
      // (menu_visibility) applies on top.
      featureFlag: submenuFeatureFlag("data-management", "financial-statements"),
    },
    {
      key: "models",
      label: "Models",
      to: "/data-management/models",
      icon: <IconCode size="1rem" stroke={1.5} />,
      match: (p) => p.startsWith("/data-management/models"),
      // Ships dark behind `data_modeling` (plan 2026-10-21-customer-data-
      // modeling-platform); the routes answer OWNER/ADMIN only.
      accountRoles: ["OWNER", "ADMIN"],
      featureFlag: submenuFeatureFlag("data-management", "models"),
    },
  ],
  activation: [
    {
      key: "segments",
      label: "Segments",
      to: "/activation/segments",
      icon: <IconFilter size="1rem" stroke={1.5} />,
      match: (p) => p.startsWith("/activation/segments") && !p.startsWith("/activation/segments/discovery"),
    },
    {
      key: "discovery",
      label: "Discovery",
      to: "/activation/segments/discovery",
      icon: <IconSparkles size="1rem" stroke={1.5} />,
      match: (p) => p.startsWith("/activation/segments/discovery"),
    },
    {
      key: "profiles",
      label: "Profiles",
      to: "/activation/profiles",
      icon: <IconUserCircle size="1rem" stroke={1.5} />,
      match: (p) => p.startsWith("/activation/profiles"),
    },
    {
      key: "campaigns",
      label: "Campaigns",
      to: "/activation/campaigns",
      icon: <IconShoppingBag size="1rem" stroke={1.5} />,
      match: (p) => p.startsWith("/activation/campaigns"),
    },
    {
      key: "surveys",
      label: "Surveys",
      to: "/activation/surveys",
      icon: <IconMessageQuestion size="1rem" stroke={1.5} />,
      match: (p) => p.startsWith("/activation/surveys"),
    },
    {
      key: "journeys",
      label: "Journeys",
      to: "/activation/journeys",
      icon: <IconRoute size="1rem" stroke={1.5} />,
      match: (p) => p.startsWith("/activation/journeys"),
      // Ships dark (plan 2026-10-02-journeys-v1): the routes redirect while
      // the flag is off, so the item must not point at a redirect.
      featureFlag: submenuFeatureFlag("activation", "journeys"),
    },
  ],
  automations: [
    {
      key: "monitors",
      label: "Monitors",
      to: "/automations",
      icon: <IconRocket size="1rem" stroke={1.5} />,
      match: (p) => p === "/automations" || p.startsWith("/automations/new") || (p.startsWith("/automations/") && !p.startsWith("/automations/alerts") && !p.startsWith("/automations/website-scan")),
    },
    {
      key: "alerts",
      label: "Alerts",
      to: "/automations/alerts",
      icon: <IconBell size="1rem" stroke={1.5} />,
      match: (p) => p.startsWith("/automations/alerts"),
    },
    {
      key: "website-scan",
      label: "Website Scan",
      to: "/automations/website-scan",
      icon: <IconShieldSearch size="1rem" stroke={1.5} />,
      match: (p) => p.startsWith("/automations/website-scan"),
      // Soft-launch: hidden until `website_scan_enabled` is set per account.
      // The submenu gate (menu_visibility) applies on top; the flag is the
      // release switch (R1-ARCH-2) so null-config accounts don't see it early.
      featureFlag: submenuFeatureFlag("automations", "website-scan"),
    },
  ],
};

type Props = {
  section: Section;
  collapsed: boolean;
};

export default function SectionSubNav({ section, collapsed }: Props) {
  const pathname = useTenantPathname();
  const access = useSectionAccess();
  const { activeAccount, user } = useAuth();
  const flags = activeAccount?.featureFlags;

  const items = SECTION_ITEMS[section]
    .map((item) => ({ item, access: access.submenu(section, item.key) }))
    .filter(({ item, access: a }) =>
      isNavItemShown(item, {
        visible: a.shown,
        flags,
        role: user?.activeAccount?.role,
        isSuperadmin: !!user?.isSuperadmin,
      })
    )
    .map(({ item, access: a }) => ({ ...item, hiddenFromCustomer: a.hiddenFromCustomer }));

  if (items.length === 0) return null;

  if (collapsed) {
    return (
      <Stack gap={2} align="center" px={4} py="xs">
        {items.map((item) => (
          <Tooltip
            key={item.to}
            label={item.hiddenFromCustomer ? `${item.label} · Hidden from customer` : item.label}
            position="right"
          >
            <ActionIcon
              component={Link}
              to={item.to}
              variant={item.match(pathname) ? "light" : "subtle"}
              color="blue"
              size={40}
            >
              {item.icon}
            </ActionIcon>
          </Tooltip>
        ))}
      </Stack>
    );
  }

  return (
    <Stack gap={0}>
      {items.map((item) => (
        <NavLink
          key={item.to}
          component={Link}
          to={item.to}
          label={item.label}
          leftSection={item.icon}
          rightSection={item.hiddenFromCustomer ? <HiddenFromCustomerMark /> : undefined}
          active={item.match(pathname)}
          color="blue"
          py={6}
          styles={{ root: { borderRadius: 0 } }}
        />
      ))}
    </Stack>
  );
}
